import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'

import Lockup from './Lockup.jsx'
import { PresenceButton, PresenceDot, PresenceList } from './Presence.jsx'
import { build, topLevelSections } from '../content.js'
import { useDirectory } from '../directory.js'
import ActivityFeed from '../portal/ActivityFeed.jsx'
import { useMovie } from '../portal/movie.js'
import { useMusicOnce } from '../portal/music.js'
import { joinNames, peopleBySection, peopleFor, usePresence } from '../portal/presence.js'
import { useNow } from '../portal/useNow.js'
import { useWatch } from '../portal/watch.js'
import { LOADING, SIGNED_IN, signInHref, useSession } from '../session.js'

// The shell: sticky top bar on desktop, four bottom tabs on mobile, footer.
//
// "Desktop nav ≠ mobile nav" is one of the handoff's stated decisions, and the
// tab set is the DESTINATION set — a fifth section means a "More" tab, not a
// fifth icon. TAB_PRIORITY below is capped at four for that reason.
//
// 🔴 THIS BLOCK USED TO SAY "and the test in src/test/design-system.test.js
// holds it there." IT DID NOT. Measured while changing it: no test in the suite
// asserted the tab count, and none ever had — the sentence was a claim about a
// safety net that was not there, read as reassurance by everyone who came past
// it. That is CLAUDE.md gotcha 6 in miniature, in a code comment. The assertion
// now exists (src/test/app.test.jsx, "the tab bar"), so the sentence is true for
// the first time.
//
// 🔴 THE SIGN-IN CONTROL IS HERE NOW, and the instruction for where to put it
// was written before it existed. This block used to read:
//
//   "No 'Sign in with Discord' control anywhere. The designed shell has one,
//    and it is deliberately absent: authentication needs an ADR before
//    implementation, and a button that opens nothing is auth scaffolding
//    wearing a disguise. When the ADR lands it goes in the top bar's right
//    slot, bordered, never the ember fill — signing in is a door, not the point
//    of the page."
//
// ADR-0003 was accepted on 2026-08-14 and P5-3 T3 built the flow, so the
// condition is met and the placement instruction is followed exactly: top bar,
// right slot, bordered, no ember fill. It is a real link to a real Function —
// the thing that made the old rule right is the thing that retired it.
//
// The control is an <a>, never a react-router <Link>: /auth/login is a Pages
// Function, and a client-side navigation would render a 404 from the SPA
// instead of leaving the app. Signing out is a <form method="post"> for the
// same reason plus one more — see functions/auth/logout.js on why a GET logout
// is a hole.

// The tab bar's destinations, in priority order, and the first four this viewer
// can actually open become the tabs. P6-104.
//
// 🔴 IT WAS A FIXED LIST OF FOUR AND THAT LEFT /trips UNREACHABLE ON A PHONE.
// The top bar's nav is `display: none` below 768px, so on mobile the tab bar is
// the whole navigation — and the one page that is actually a member's own stuff
// was in neither. Measured, not theorised: the old TABS was
// ['landing', 'architecture', 'commands', 'docs'] and nothing else linked to
// /trips. A signed-in member on a phone could reach the crew page only by typing
// the URL.
//
// The corrected design brief is why this is worth a rule rather than a swap:
// "the most frequent users we should expect to be server members — it should
// feel like an integration with the server", and a member reaching for this
// mid-conversation is already on their phone. So the four tabs are the four
// most useful DESTINATIONS FOR WHOEVER IS ASKING:
//
//   signed out   Home · Docs · Commands · Releases
//   signed in    Home · Docs · Commands · Trips        (Releases drops off)
//
// Priority order, filtered by what the viewer may open, sliced to four. Both
// cases are asserted in src/test/app.test.jsx — including that the count never
// exceeds four, which is the rule the handoff actually stated.
const TAB_PRIORITY = ['landing', 'docs', 'commands', 'trips', 'releases']
const TAB_LIMIT = 4

// ═══════════════════════════════════════════════════════════════════════════
// 🔴 P6-242b / P6-285: THE HEADER HAS A SECOND ROW — PRESENCE AND THE FEED
// ═══════════════════════════════════════════════════════════════════════════
//
// Two Claude Design handoffs are the spec (`docs/design-briefs/portal/`):
// `HANDOFF-presence.md` and `HANDOFF-header-feed.md`. Four rules in them are
// MEASURED, not taste, and each is held by `src/test/viewport.test.js` in a
// real browser rather than by this comment:
//
//   1. the typeset wordmark is hidden ONLY at ≤336px and 768–1023px (the live
//      P6-221 rule in app.css, which this file does not touch);
//   2. presence enters the TOP BAR at 1120px and not one pixel earlier — below
//      that it is the second row's;
//   3. the nav never wraps;
//   4. presence unavailable renders NOTHING, and with the feed also unavailable
//      there is no second row at all.
//
// ⚠️ A SIGNED-OUT SHELL ASKS NOTHING. The feed's reads (`/api/movie`, `/api/music`,
// `/api/watch`, the member directory) and presence's heartbeat all live below
// `SignedInStrip`, which is rendered only for a signed-in member — a hook cannot
// be called conditionally, so the condition is a component boundary. ADR-0010's
// closing section says it in as many words: *"a signed-out shell fetches nothing."*
//
// ⚠️ THERE IS NO THEME CONTROL IN THIS BAR, AND THE HANDOFFS DRAW ONE. The design
// has a 44px theme button that costs the row 56px (a hit target and a 12px gap).
// Light mode is accepted and not built (`tokens.css`), so the control is not
// here and the bar has 56px the handoff did not have. `viewport.test.js` adds
// that 56px back as a stand-in and asserts the nav still does not wrap — so the
// day the control lands, rule 3 has already been checked against it.

/**
 * The second row of the header, for a signed-in member: the presence strip and
 * the feed, plus the list that drops in under them.
 *
 * It owns the feed's reads and the member directory the rows are named
 * through. `PortalHome` reads `/api/movie` and the directory for its own panel
 * as it always did, so on `/` there are two readers of each — the directory
 * hook's own note says one request per surface that wants it and to reconsider
 * at a third.
 */
function SignedInStrip({ userId, shown, people, open, onToggle }) {
  const directory = useDirectory()
  const movie = useMovie()
  const music = useMusicOnce()
  const watch = useWatch()
  const now = useNow()

  const lead = shown ? (
    <PresenceButton
      variant="strip"
      people={people}
      youId={userId}
      open={open === 'presence'}
      onToggle={(element) => onToggle('presence', element)}
    />
  ) : null

  return (
    <>
      <ActivityFeed
        movie={movie}
        music={music}
        watch={watch}
        directory={directory}
        userId={userId}
        now={now}
        open={open}
        onToggle={onToggle}
        lead={lead}
      />
      {shown && open === 'presence' ? <PresenceList people={people} youId={userId} /> : null}
    </>
  )
}

/** The words a nav link carries when somebody else is on it — `title` for the
 * pointer and the accessible name for everybody. Never visible text: the dot is
 * decoration and the names are not for the bar to spell out. */
function herePhrase(people) {
  return `${joinNames(people)} ${people.length === 1 ? 'is' : 'are'} here`
}

export default function Layout({ children }) {
  const session = useSession()
  const location = useLocation()
  const signedIn = session.status === SIGNED_IN
  const userId = signedIn ? session.userId : null

  // Presence: unavailable by default, silent on failure, nothing asked of a
  // signed-out shell. See `portal/presence.js`.
  const presence = usePresence({ enabled: signedIn, pathname: location.pathname })
  const people = useMemo(
    () => peopleFor({ present: presence.present, youId: userId, pathname: location.pathname }),
    [presence.present, userId, location.pathname],
  )
  // Available means the Function ANSWERED (an empty crew is an answer — it draws
  // "just you") and there is somebody to draw. Everything below keys on this one
  // value, so the bar button, the strip, the list and the dots cannot disagree.
  const shown = presence.available && people.length > 0
  const bySection = useMemo(
    () => (shown ? peopleBySection(people, userId) : new Map()),
    [shown, people, userId],
  )

  // ONE open list at a time — the handoff's rule, held structurally by this being
  // one value rather than two flags. `opener` is whichever button opened it, so
  // Escape and a second press hand focus back to the thing the member pressed
  // (the in-bar and in-strip presence buttons are different elements).
  const [open, setOpen] = useState('none')
  const opener = useRef(null)
  const toggle = useCallback((which, element) => {
    opener.current = element ?? null
    setOpen((was) => (was === which ? 'none' : which))
  }, [])
  useEffect(() => {
    // A list belongs to the page it was opened on, and to a session that exists.
    setOpen('none')
  }, [location.pathname, signedIn])
  useEffect(() => {
    if (open === 'none') return undefined
    function onKeyDown(event) {
      if (event.key !== 'Escape') return
      setOpen('none')
      opener.current?.focus()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open])

  // Tier 1 is always listed. A tier-3 section is listed only to somebody who
  // can open it — an anonymous visitor's page never advertises the crew routes,
  // and never offers a link that only bounces them to a sign-in.
  //
  // 🔴 TOP-LEVEL ONLY. `topLevelSections` drops anything with a `parent`, which
  // is what keeps the nav at five items while /docs has seven pages under it.
  // Filtering the whole registry here would put every feature page in the top
  // bar, which is the crammed-into-one-surface problem one level up.
  const navSections = topLevelSections.filter((section) => section.tier === 1 || signedIn)
  const tabs = TAB_PRIORITY.map((id) => navSections.find((s) => s.id === id))
    .filter(Boolean)
    .slice(0, TAB_LIMIT)
  // P6-183 moved the construction into session.js. The landing page's own
  // sign-in call has to produce the identical URL, and two copies of it is the
  // seam a drift lives in — see the note on signInHref.
  const signIn = signInHref(location)

  return (
    <div className="d2d-shell">
      <header className="d2d-topbar">
        <div className="d2d-page d2d-topbar__inner">
          {/* Inside the banner landmark deliberately: a skip link floating
              outside every landmark is content axe's `region` rule flags. */}
          <a className="d2d-skip" href="#main">
            Skip to content
          </a>
          <NavLink
            to="/"
            className={() => 'd2d-topbar__lockup'}
            aria-label="D A Y 2 D A Y, home"
          >
            <Lockup size="nav" />
          </NavLink>

          <nav className="d2d-topbar__sections" aria-label="Sections">
            <ul className="d2d-topbar__nav">
              {navSections.map((section) => {
                const here = bySection.get(section.id) ?? []
                return (
                  <li key={section.id}>
                    <NavLink
                      to={section.path}
                      end={section.path === '/'}
                      className={() => 'd2d-navlink'}
                      title={here.length > 0 ? herePhrase(here) : undefined}
                      aria-label={
                        here.length > 0 ? `${section.title}, ${herePhrase(here)}` : undefined
                      }
                    >
                      {section.title}
                      <PresenceDot people={here} />
                    </NavLink>
                  </li>
                )
              })}
            </ul>
          </nav>

          <div className="d2d-topbar__end">
            {/* Presence in the BAR, from 1120px up (the stylesheet decides which side
                of the breakpoint paints it). Absent entirely — no placeholder, no
                reserved space — until the endpoint has answered with somebody. It
                comes BEFORE the rest of the group, the handoff's order, so the
                theme control that is coming sits after it. */}
            {shown ? (
              <PresenceButton
                variant="bar"
                people={people}
                youId={userId}
                open={open === 'presence'}
                onToggle={(element) => toggle('presence', element)}
              />
            ) : null}

            {/* Status, not a claim about uptime: the word carries the meaning and
                the dot is decoration. Under reduced motion nothing here animates,
                so no information lives only in movement. */}
            <span className="d2d-status d2d-micro">
              <span className="d2d-status__dot" aria-hidden="true" />
              SITE UP
            </span>

            {/* Nothing is rendered until the answer arrives. Showing "Sign in"
                to a signed-in member for half a second, or the reverse, is the
                shell asserting something it does not know yet. */}
            {session.status === LOADING ? null : signedIn ? (
              <form method="post" action="/auth/logout">
                <button type="submit" className="d2d-authlink d2d-micro">
                  SIGN OUT
                </button>
              </form>
            ) : (
              // 🔴 TWO LABELS, ONE ACCESSIBLE NAME — P6-104, and this control is
              // the reason the whole site overflowed a phone.
              //
              // Measured against production at 375×812 on 2026-08-22:
              // `.d2d-authlink` is `white-space: nowrap`, 234px wide, right edge
              // at 454px on a 375px viewport. EVERY page overflowed by 79px from
              // this one element, on the site whose primary reader the corrected
              // design brief says is "already on their phone, in Discord, and
              // switching out for ten seconds".
              //
              // The fix is a shorter VISIBLE label below the breakpoint, not a
              // shorter control: the a11y name is carried by aria-label and is
              // "Sign in with Discord" at every width, so a screen reader hears
              // the same sentence a wide screen shows. The spans are
              // aria-hidden precisely so that name cannot change with the
              // viewport — CSS decides which one is painted, and the accessible
              // name is decided here, once.
              //
              // ⚠️ WCAG 2.5.3 (Label in Name) is why the short label is "SIGN IN"
              // and not "LOG IN" or an icon: the visible text has to be
              // contained in the accessible name, or voice control cannot
              // activate the thing a user can see.
              <a
                className="d2d-authlink d2d-micro"
                href={signIn}
                aria-label="Sign in with Discord"
              >
                <span className="d2d-authlink__full" aria-hidden="true">
                  SIGN IN WITH DISCORD
                </span>
                <span className="d2d-authlink__short" aria-hidden="true">
                  SIGN IN
                </span>
              </a>
            )}
          </div>
        </div>

        {signedIn ? (
          <SignedInStrip
            userId={userId}
            shown={shown}
            people={people}
            open={open}
            onToggle={toggle}
          />
        ) : null}
      </header>

      <main id="main" className="d2d-main">
        {children}
      </main>

      <footer className="d2d-footer">
        <div className="d2d-page">
          <Lockup size="footer" />
          <p className="d2d-footer__statement">
            A private Discord server. This site is the public, read-only part of it — no accounts,
            no data collected, nothing to sign up for.
          </p>

          <div className="d2d-footer__cols">
            <div>
              <p className="d2d-footer__head d2d-micro">The site</p>
              <ul>
                {topLevelSections
                  .filter((section) => section.tier === 1)
                  .map((section) => (
                    <li key={section.id}>
                      <NavLink to={section.path} className={() => 'd2d-footer__link'}>
                        {section.title}
                      </NavLink>
                    </li>
                  ))}
              </ul>
            </div>
            <div>
              <p className="d2d-footer__head d2d-micro">The bot</p>
              <ul>
                <li>
                  <span className="d2d-footer__link">Runs in Discord</span>
                </li>
              </ul>
            </div>
          </div>

          <p className="d2d-footer__build d2d-micro">
            Built from <code data-testid="build-commit">{build.commit}</code> ·{' '}
            <time dateTime={build.generatedAt}>{build.generatedAt}</time>
          </p>
        </div>
      </footer>

      <nav className="d2d-tabbar" aria-label="Main">
        <ul className="d2d-tabbar__list">
          {tabs.map((section) => {
            const here = bySection.get(section.id) ?? []
            const label = section.short ?? section.title
            return (
              <li key={section.id}>
                <NavLink
                  to={section.path}
                  end={section.path === '/'}
                  className={() => 'd2d-tab'}
                  title={here.length > 0 ? herePhrase(here) : undefined}
                  aria-label={here.length > 0 ? `${label}, ${herePhrase(here)}` : undefined}
                >
                  {/* The dot sits BESIDE the 18×3 icon and takes no room of its own —
                      it is absolutely placed inside the icon's box — so a tab does
                      not shift when somebody arrives on it. */}
                  <span className="d2d-tab__icon" aria-hidden="true">
                    <PresenceDot people={here} />
                  </span>
                  <span className="d2d-micro">{label}</span>
                </NavLink>
              </li>
            )
          })}
        </ul>
      </nav>
    </div>
  )
}
